import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import { usePermission } from '../hooks/usePermission';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import { FIELD } from '../components/ui/tokens';
import { EmptyState, ErrorState, LoadingIndicator } from '../components/ui/States';
import { createPaymentRun, listPaymentRuns, paymentRunRefusalMessage } from '../lib/paymentRunsApi';
import type { RunListPage, RunStatus } from '../lib/paymentRunsApi';
import { formatDate } from '../utils/dateFormat';
import { browserToday } from '../features/vendorInvoices/paymentOnReceiptForm';
import { formatReportDay } from '../features/accountsPayableReports/format';
import { RUN_STATUS_LABEL, RUN_STATUS_TONE, moneyByCurrency } from '../features/paymentRuns/runDisplay';
import DateInput from "../components/DateInput";

/**
 * Payment runs (D49 phase 4): the history of runs and the way to start one. A run picks what is
 * due by a date, is reviewed by supplier, and records the payments in one go. Starting one only
 * SAVES a draft (every open bill due by the day, apart from bills on hold); nothing is paid until
 * "Record payments" on the run itself.
 */

const STATUSES: ReadonlyArray<{ value: RunStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All runs' },
  { value: 'draft', label: 'Drafts' },
  { value: 'partial', label: 'Partly recorded' },
  { value: 'completed', label: 'Recorded' },
  { value: 'cancelled', label: 'Cancelled' },
];

const CURRENCIES = ['TTD', 'USD'];
const CELL = 'px-3 py-2 align-top';
const LABEL = 'mb-1 block text-xs font-medium text-slate-600';

export const PaymentRunsPage: React.FC = () => {
  const navigate = useNavigate();
  // Starting a run saves one, which needs the Financials CREATE right (as recording a payment does).
  const { canCreate } = usePermission('financials');
  const [status, setStatus] = useState<RunStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<RunListPage | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [reloads, setReloads] = useState(0);
  const latest = useRef(0);

  const [starting, setStarting] = useState(false);
  const [dueBy, setDueBy] = useState(() => browserToday());
  const [dueByValid, setDueByValid] = useState(true);
  const [currency, setCurrency] = useState('');
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    latest.current += 1;
    const mine = latest.current;
    setState('loading');
    listPaymentRuns({ status, page })
      .then((next) => {
        if (mine !== latest.current) return;
        setData(next);
        setState('ready');
      })
      .catch(() => {
        if (mine !== latest.current) return;
        setState('error');
      });
  }, [status, page, reloads]);

  const start = async () => {
    // A typed date the box refused is not in dueBy, which still holds the
    // previous one: starting now would draft the run for the wrong day.
    if (!dueByValid) {
      setMessage('Bills due by is not a valid date. Type dd/mm/yyyy, or clear it and pick again.');
      return;
    }
    setCreating(true);
    setMessage('');
    try {
      const run = await createPaymentRun({ dueBy, ...(currency ? { currency } : {}) });
      navigate(`/accounts/payment-runs/${run.id}`);
    } catch (err) {
      setMessage(paymentRunRefusalMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const back = (
    <Button variant="secondary" icon={<ArrowLeft aria-hidden="true" className="h-4 w-4" />} onClick={() => navigate('/accounts')}>
      Back
    </Button>
  );

  return (
    <div className="flex h-full flex-col overflow-auto bg-brand-bg">
      <PageHeader
        back={back}
        title="Payment runs"
        subtitle="For paying several suppliers at once: pick what is due by a date, review it by supplier, and record the payments in one go. Paying a single bill? Use Pay on the Accounts list instead -- it is faster."
        actions={
          canCreate ? (
            <Button variant="primary" icon={<Plus aria-hidden="true" className="h-4 w-4" />} onClick={() => setStarting((v) => !v)}>
              New payment run
            </Button>
          ) : undefined
        }
      />

      <main className="flex-1 space-y-4 px-4 py-4 sm:px-8">
        {starting && canCreate ? (
          <section aria-label="Start a payment run" className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
              <div>
                <label htmlFor="new-run-due-by" className={LABEL}>Bills due by</label>
                <DateInput
                  id="new-run-due-by"
                  validity={setDueByValid}
                  value={dueBy}
                  onChange={(v) => setDueBy(v)}
                  className={`${FIELD} h-9 border-slate-300 bg-white`}
                />
              </div>
              <div>
                <label htmlFor="new-run-currency" className={LABEL}>Currency</label>
                <select
                  id="new-run-currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className={`${FIELD} h-9 min-w-[9rem] border-slate-300 bg-white`}
                >
                  <option value="">All currencies</option>
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <Button variant="primary" onClick={() => void start()} loading={creating} disabled={!dueBy || !dueByValid}>
                Start run
              </Button>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              This saves a draft with every open bill due on or before that day, at what it owes. Bills on hold or
              disputed are not ticked. Nothing is paid until you record the run.
            </p>
            {message ? (
              <p role="alert" className="mt-2 text-sm text-red-700">
                {message}
              </p>
            ) : null}
          </section>
        ) : null}

        <div>
          <label htmlFor="run-status-filter" className={LABEL}>Show</label>
          <select
            id="run-status-filter"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as RunStatus | 'all');
              setPage(1);
            }}
            className={`${FIELD} h-9 min-w-[10rem] border-slate-300 bg-white`}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        {state === 'loading' && !data ? <LoadingIndicator label="Loading payment runs..." /> : null}
        {state === 'error' ? <ErrorState message="The payment runs could not be loaded." onRetry={() => setReloads((n) => n + 1)} /> : null}

        {data && data.items.length === 0 && state !== 'error' ? (
          <EmptyState
            title="No payment runs yet."
            detail="Start one to pay everything due by a date, supplier by supplier."
          />
        ) : null}

        {data && data.items.length > 0 ? (
          <div className="relative overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="data-table w-full min-w-[52rem] text-left text-sm">
              <caption className="sr-only">Payment runs, newest first</caption>
              <thead className="bg-slate-50">
                <tr className="text-xs text-slate-600">
                  <th scope="col" className={CELL}>Run</th>
                  <th scope="col" className={CELL}>Status</th>
                  <th scope="col" className={CELL}>Due by</th>
                  <th scope="col" className={CELL}>Payment date</th>
                  <th scope="col" className={CELL}>Suppliers</th>
                  <th scope="col" className={CELL}>Total</th>
                  <th scope="col" className={CELL}>Created</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((run) => (
                  <tr key={run.id}>
                    <td className={`${CELL} font-medium`}>
                      <button
                        type="button"
                        className="text-primary-600 hover:underline"
                        onClick={() => navigate(`/accounts/payment-runs/${run.id}`)}
                        aria-label={`Open payment run ${run.runNumber}`}
                      >
                        {run.runNumber}
                      </button>
                    </td>
                    <td className={CELL}>
                      <span className={`rounded border px-2 py-0.5 text-xs font-medium ${RUN_STATUS_TONE[run.status]}`}>
                        {RUN_STATUS_LABEL[run.status]}
                      </span>
                    </td>
                    <td className={`${CELL} whitespace-nowrap`}>{formatReportDay(run.dueBy)}</td>
                    <td className={`${CELL} whitespace-nowrap`}>{formatReportDay(run.paymentDate)}</td>
                    <td className={CELL}>
                      {run.recordedCount} of {run.supplierCount} recorded
                      {run.failedCount ? <span className="text-red-700">, {run.failedCount} failed</span> : null}
                    </td>
                    <td className={`${CELL} tabular-nums`}>{moneyByCurrency(run.totals)}</td>
                    <td className={`${CELL} text-xs text-slate-600`}>
                      {formatDate(run.createdAt, '--')}
                      <div>{run.createdBy}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {data && data.pages > 1 ? (
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>
              Page {data.page} of {data.pages}
            </span>
            <span className="flex gap-2">
              <Button size="sm" variant="secondary" disabled={data.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Button size="sm" variant="secondary" disabled={data.page >= data.pages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </span>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default PaymentRunsPage;
